import { useEffect, useRef } from 'react'

/**
 * Listens for a barcode scanner anywhere on the page.
 *
 * A USB or Bluetooth wedge scanner is just a keyboard that types very fast and finishes with
 * Enter. Distinguishing it from a human is a timing question: keystrokes arriving faster than
 * a person can type, in a burst, ending in Enter. Listening globally rather than on a focused
 * input matters because a cashier's focus is wherever they last tapped, and a scan has to work
 * regardless.
 */
export function useScanner(onScan: (code: string) => void, enabled = true) {
  const buffer = useRef('')
  const lastKeyAt = useRef(0)
  const handler = useRef(onScan)
  handler.current = onScan

  useEffect(() => {
    if (!enabled) return

    /** Gap above which two keystrokes are treated as separate human typing, in ms. */
    const HUMAN_GAP_MS = 60
    /** Shorter than this and it is a keypress, not a barcode. */
    const MIN_LENGTH = 4

    function onKeyDown(event: KeyboardEvent) {
      const now = Date.now()
      const gap = now - lastKeyAt.current
      lastKeyAt.current = now

      if (event.key === 'Enter') {
        const code = buffer.current
        buffer.current = ''

        if (code.length >= MIN_LENGTH) {
          // Stops the Enter also submitting whatever form happens to have focus.
          event.preventDefault()
          handler.current(code)
        }
        return
      }

      // Anything that is not a single printable character cannot be part of a barcode.
      if (event.key.length !== 1) return

      // A slow keystroke means a human started typing, so the buffer restarts from here.
      buffer.current = gap > HUMAN_GAP_MS ? event.key : buffer.current + event.key
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [enabled])
}
